import { Card, CardHeader, H5 } from "@sellar-io/components";
import { OrderGetResponseType } from "@sellar-io/api-v2/types";
import { formatCurrency } from "@sellar-io/common";
import React, { ReactNode, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { useSellarUser } from "../../../hooks";
import { OrderContentConnectionsType } from "../../../pages/order/OrderContent/OrderContent";
import { Alert } from "../../alert/Alert";
import { SupplierOnly } from "../../authFilter/SupplierOnly";
import { FullDutyBadge } from "../../badge/FullDutyBadge";
import { OrderCreateInvoiceDialog } from "../../../pages/order/modals/OrderCreateInvoiceDialog";
import { DropdownLegacy } from "../../dropdown/DropdownLegacy";
import { ProfilePicture } from "../../profilePicture/ProfilePicture";
import { OrderActionPanel } from "../OrderActionPanel";
import { OrderSummaryHeaderActionButtons } from "../OrderSummaryHeaderActionButtons";
import { OrderSummaryStatusCardContent } from "./OrderSummaryStatusCardContent";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";
import { sanitizeEmailArray } from "@sellar-io/common";
import { PaymentTermsIndicator } from "../../paymentTypeIndicator/PaymentTermsIndicator";
import { SellarCreditPaymentTypeIndicator } from "../../paymentTypeIndicator/SellarCreditPaymentTypeIndicator";
import { getOrderStage } from "../helpers";
import { useMediaQuery } from "usehooks-ts";
import { BuyerOnly } from "~/components/authFilter/BuyerOnly";

export const OrderSummaryStatusContainer = ({
  order,
  buyerEmails,
  onBusinessNameClicked,
  connection,
  dropDownChildren,
  onVatInvoiceClick,
}: {
  order: OrderGetResponseType;
  buyerEmails: string[];
  onBusinessNameClicked?: () => void;
  connection?: OrderContentConnectionsType;
  dropDownChildren?: ReactNode;
  onVatInvoiceClick?: () => void;
}): React.ReactElement => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useSellarUser();
  const [messageType, setMessageType] = useState<string>();
  const [invoiceDialogActive, setInvoiceDialogActive] = useState(false);
  const isImplicitProformaEnabled =
    useRemoteFeatureFlags().isActive("implicitProforma");
  const buyerName = order.Retailer.Business.name;
  const supplierName = order.Supplier.Business.name;
  const invoiceCreationStage = order.Supplier.invoiceCreationStage;
  const showSellarCreditFeatures = order.paymentType === "sellarCredit";
  const isDirector = Boolean(user?.legalEntityAdminList.length);
  const isUserSellarAdmin = Boolean(user?.type === "admin");
  const canAccessTab = isDirector || isUserSellarAdmin;
  const userType = business?.Retailer ? "buyer" : "supplier";
  const requestedPaymentTermsPending =
    connection?.PaymentTermsRequest?.status === "pending"
      ? connection.PaymentTermsRequest
      : null;
  const stage = () => getOrderStage(order);
  const logoUrl =
    (business?.Supplier
      ? order.Retailer.Business.logoUrl
      : order.Supplier.Business.logoUrl) ?? "";

  const isDesktop = useMediaQuery("(min-width: 800px)");

  /* placeholder for now as there'll be more message types as we go through the next few tickets*/
  const { message, title } = (() => {
    switch (messageType) {
      case "invoiceSent":
        return {
          title: "Invoice sent successfully",
          message: `Delivered to: ${buyerEmails[0]}. ${buyerName} have been notified and can also access their invoice from within the Sellar app.`,
        };
      case "reminderSent":
        return {
          title: undefined,
          message: `Delivered to: ${buyerEmails[0]}`,
        };
      default:
        return { title: undefined, message: undefined };
    }
  })();
  return (
    <>
      {message && (
        <Alert
          className={"w-full"}
          type={"success"}
          title={title}
          message={message}
          onDismiss={() => {
            setMessageType(undefined);
          }}
        />
      )}
      <Card className="bg-white">
        <CardHeader className={twMerge("bg-white", "flex flex-col gap-2 p-4")}>
          <div className="flex items-center justify-between">
            <div className="flex flex-row items-center gap-2">
              <ProfilePicture
                logoUrl={logoUrl}
                name={business?.Supplier ? buyerName : supplierName}
                size={"small"}
              />

              <div className="flex flex-col">
                <div className="flex flex-row items-center gap-2">
                  <H5>
                    <span
                      className={twMerge(
                        onBusinessNameClicked && "cursor-pointer",
                      )}
                      onClick={onBusinessNameClicked}
                    >
                      {business?.Retailer ? supplierName : buyerName}
                    </span>{" "}
                    - {formatCurrency(order.price, order.currency)}
                  </H5>
                  <SupplierOnly>
                    {order.hasNoDraughtDutyRelief && (
                      <FullDutyBadge className={"gap-0"} />
                    )}
                  </SupplierOnly>
                </div>
                <div className="text-sm text-zinc-500">
                  Order <span className="text-zinc-800">#{order.id}</span>{" "}
                  {order.purchaseOrderNumber && (
                    <>
                      • PO number{" "}
                      <span className="text-zinc-800">
                        {order.purchaseOrderNumber}
                      </span>{" "}
                    </>
                  )}
                  {order.customInvoiceNumber && (
                    <>
                      • Invoice ID{" "}
                      <span className="text-zinc-800">
                        {order.customInvoiceNumber}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-row items-center gap-2">
              {isImplicitProformaEnabled &&
                isDesktop &&
                (showSellarCreditFeatures ? (
                  <BuyerOnly>
                    <SellarCreditPaymentTypeIndicator
                      canAccessTab={canAccessTab}
                      orderStage={stage()}
                      userType={userType}
                    />
                  </BuyerOnly>
                ) : (
                  <PaymentTermsIndicator
                    connection={connection}
                    invoiceCreationStage={invoiceCreationStage}
                    paymentTermsRequest={requestedPaymentTermsPending}
                    paymentType={order.paymentType}
                    isImplicitProformaEnabled={isImplicitProformaEnabled}
                  />
                ))}

              <div className={"hidden md:block"}>
                <OrderSummaryHeaderActionButtons
                  orderId={order.id}
                  paymentDue={order.paymentDue}
                  cancelledBy={order.cancelledBy}
                  cancelledAt={order.cancelledAt}
                  voidedAt={order.voidedAt}
                  confirmedAt={order.confirmedAt}
                  dispatchedAt={order.dispatchedAt}
                  deliveredAt={order.deliveredAt}
                  invoiceFinalizedAt={order.invoiceFinalizedAt}
                  paymentType={order.paymentType}
                  deliveryPlannedAt={order.deliveryPlannedAt ?? null}
                  paymentStatus={order.paymentStatus}
                  stripeInvoiceId={order.stripeInvoiceId}
                  fulfillmentType={order.fulfillmentType}
                  retailerId={order.RetailerId}
                  orderItems={order.OrderItems}
                  connectionId={connection?.id}
                  invoiceCreationStage={invoiceCreationStage}
                  invoiceIssuedAt={order.invoiceIssuedAt}
                />
              </div>

              {dropDownChildren && (
                <DropdownLegacy className={"print:hidden"}>
                  {dropDownChildren}
                </DropdownLegacy>
              )}
            </div>
          </div>

          {!isDesktop && (
            <div className="w-full">
              {isImplicitProformaEnabled &&
                (showSellarCreditFeatures ? (
                  <SellarCreditPaymentTypeIndicator
                    canAccessTab={canAccessTab}
                    orderStage={stage()}
                    userType={userType}
                  />
                ) : (
                  <PaymentTermsIndicator
                    connection={connection}
                    invoiceCreationStage={invoiceCreationStage}
                    paymentTermsRequest={requestedPaymentTermsPending}
                    paymentType={order.paymentType}
                    isImplicitProformaEnabled={isImplicitProformaEnabled}
                  />
                ))}
            </div>
          )}

          <div className={"md:hidden lg:hidden xl:hidden"}>
            <OrderSummaryHeaderActionButtons
              orderId={order.id}
              paymentDue={order.paymentDue}
              cancelledBy={order.cancelledBy}
              cancelledAt={order.cancelledAt}
              voidedAt={order.voidedAt}
              confirmedAt={order.confirmedAt}
              dispatchedAt={order.dispatchedAt}
              deliveredAt={order.deliveredAt}
              invoiceFinalizedAt={order.invoiceFinalizedAt}
              paymentType={order.paymentType}
              deliveryPlannedAt={order.deliveryPlannedAt ?? null}
              paymentStatus={order.paymentStatus}
              stripeInvoiceId={order.stripeInvoiceId}
              fulfillmentType={order.fulfillmentType}
              retailerId={order.RetailerId}
              orderItems={order.OrderItems}
              connectionId={connection?.id}
              invoiceCreationStage={invoiceCreationStage}
              invoiceIssuedAt={order.invoiceIssuedAt}
            />
          </div>

          {!order.cancelledAt && !order.voidedAt && (
            <OrderActionPanel
              key={order.id}
              buyerName={buyerName}
              buyerPostcode={order.Retailer.Business.DeliveryAddress.postalCode}
              invoiceFinalizedAt={order.invoiceFinalizedAt}
              orderId={order.id}
              confirmedAt={order.confirmedAt}
              paymentStatus={order.paymentStatus}
              paymentDue={order.paymentDue}
              paymentType={order.paymentType}
              paymentTerms={connection?.paymentTerms}
              dueDays={connection?.paymentDue ?? null}
              supplierName={supplierName}
              stripeInvoiceId={order.stripeInvoiceId}
              stripeAccountId={order.Supplier.stripeAccountId}
              setMessageType={setMessageType}
              fulfillmentType={order.fulfillmentType}
              deliveredAt={order.deliveredAt}
              dispatchedAt={order.dispatchedAt}
              voidedAt={order.voidedAt}
              invoiceCreationStage={invoiceCreationStage}
              invoiceIssuedAt={order.invoiceIssuedAt}
              paidAt={order.paidAt}
              price={order.price}
              paidAmount={order.paidAmount}
              hasNoDraughtDutyRelief={order.hasNoDraughtDutyRelief}
              acceptedAt={order.acceptedAt}
              deferredPaymentId={order.Payment?.deferredPaymentId ?? null}
              initiator={order.initiator}
              dispatchPlannedAt={order.dispatchPlannedAt}
              twoOrder={order.TwoOrder}
              SupplierPalletShipment={order.SupplierPalletShipment}
              supplierId={order.SupplierId}
              hasBankDetails={
                Boolean(
                  order.Supplier.SupplierPaymentInformation?.bankAccountNumber,
                ) &&
                Boolean(order.Supplier.SupplierPaymentInformation?.bankSortCode)
              }
              invoicesEnabled={order.Supplier.invoicesEnabled}
              retailerInvoiceEmails={buyerEmails}
              hasPaymentLink={Boolean(order.PaymentLink)}
              paymentLinkCode={order.PaymentLink?.code}
              isImplicitProformaEnabled={isImplicitProformaEnabled}
            />
          )}
        </CardHeader>
        <OrderSummaryStatusCardContent
          order={order}
          onVatInvoiceClick={
            isImplicitProformaEnabled
              ? () => setInvoiceDialogActive(true)
              : onVatInvoiceClick
          }
          connection={connection}
        />
      </Card>
      <OrderCreateInvoiceDialog
        active={invoiceDialogActive}
        orderId={order.id}
        onDismiss={() => setInvoiceDialogActive(false)}
        retailerInvoiceEmails={
          !!order.retailerInvoiceEmails?.length
            ? order.retailerInvoiceEmails
            : sanitizeEmailArray(order.Retailer.Business.invoiceEmails)
        }
      />
    </>
  );
};
